import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface Skill {
  id: string
  name: string
  category?: string | null
  color?: string | null
}

interface SkillsResponse {
  // Matches GET /skills envelope in apps/api/src/routes/skills/list.ts —
  // `{ skills, total, limit, offset }`. Only `skills` is consumed here.
  skills: Skill[]
}

export interface UseSkillsReturn {
  skills: Skill[]
  loading: boolean
  error: string | null
}

const EMPTY_SKILLS: Skill[] = []
const STALE_MS = 60 * 1000
let cache: { items: Skill[]; fetchedAt: number } | null = null

/**
 * Fetches the tenant's skills from GET /skills. Module-level cache (60s TTL)
 * so pickers (transfer dialogs, routing-action picker) don't refetch on
 * every open.
 */
export function useSkills(): UseSkillsReturn {
  const [skills, setSkills] = useState<Skill[]>(cache?.items ?? EMPTY_SKILLS)
  const [loading, setLoading] = useState(cache === null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<SkillsResponse>('/skills')
      const items = resp.skills ?? EMPTY_SKILLS
      cache = { items, fetchedAt: Date.now() }
      setSkills(items)
    } catch (e) {
      setError((e as Error).message)
      setSkills(EMPTY_SKILLS)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (cache !== null && Date.now() - cache.fetchedAt <= STALE_MS) {
      setSkills(cache.items)
      setLoading(false)
      return
    }
    void load()
  }, [load])

  return { skills, loading, error }
}

/** Test-only: clears the module-level cache. */
export function __resetSkillsCache(): void {
  cache = null
}
